/**
 * Cosmic Data Cache - In-memory cache with TTL for celestial calculations
 * 
 * Purpose: Reduce redundant API calls by caching cosmic data for identical
 * datetime/location combinations. Planetary positions don't change significantly
 * within the same minute, so we can safely cache for short periods.
 * 
 * Cache Key Format: `{latitude}_{longitude}_{datetime_minute}`
 * TTL: 120 seconds (positions are accurate to the minute)
 * 
 * v1.57: Increased TTL from 60s to 120s, added forceRefresh method
 */

class CosmicDataCache {
  constructor() {
    this.cache = new Map();
    this.TTL_MS = 120 * 1000; // 120 seconds (was 60)
    this.MAX_ENTRIES = 100; // Increased from 50 for better multi-location support
    this.hits = 0;
    this.misses = 0;
    this.lastRefresh = null;
  }

  /**
   * Generate a cache key from request parameters
   * Rounds datetime to the minute for consistent caching
   */
  generateKey(latitude, longitude, datetime) {
    const date = new Date(datetime);
    // Round to minute for cache key
    const minuteKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;
    // Round coordinates to 2 decimal places (city-level precision)
    const lat = latitude.toFixed(2);
    const lon = longitude.toFixed(2);
    return `${lat}_${lon}_${minuteKey}`;
  }

  /**
   * Get cached data if available and not expired
   */
  get(latitude, longitude, datetime) {
    const key = this.generateKey(latitude, longitude, datetime);
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.misses++;
      return null;
    }
    
    // Check if expired
    if (Date.now() - entry.timestamp > this.TTL_MS) {
      this.cache.delete(key);
      this.misses++;
      return null;
    }
    
    this.hits++;
    return entry.data;
  }

  /**
   * Store data in cache
   */
  set(latitude, longitude, datetime, data) {
    const key = this.generateKey(latitude, longitude, datetime);
    
    // Evict oldest entries if at capacity
    if (this.cache.size >= this.MAX_ENTRIES) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
    
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
    
    this.lastRefresh = Date.now();
  }

  /**
   * Clear all cached data
   */
  clear() {
    this.cache.clear();
    this.hits = 0;
    this.misses = 0;
    this.lastRefresh = null;
  }

  /**
   * Force refresh - clears cache and marks for refresh
   * Returns true if cache was actually cleared
   */
  forceRefresh() {
    const hadEntries = this.cache.size > 0;
    this.clear();
    return hadEntries;
  }

  /**
   * Check if cache needs refresh (no data or stale)
   */
  needsRefresh(latitude, longitude, datetime) {
    const cached = this.get(latitude, longitude, datetime);
    return !cached;
  }

  /**
   * Get cache statistics for debugging
   */
  getStats() {
    const total = this.hits + this.misses;
    return {
      entries: this.cache.size,
      hits: this.hits,
      misses: this.misses,
      hitRate: total > 0 ? ((this.hits / total) * 100).toFixed(1) + '%' : 'N/A',
      lastRefresh: this.lastRefresh ? new Date(this.lastRefresh).toISOString() : 'Never',
      ttlSeconds: this.TTL_MS / 1000
    };
  }
  
  /**
   * Prewarm cache hint - returns if a key would be a cache miss
   * Used for prefetching decisions
   */
  wouldMiss(latitude, longitude, datetime) {
    const key = this.generateKey(latitude, longitude, datetime);
    const entry = this.cache.get(key);
    if (!entry) return true;
    return Date.now() - entry.timestamp > this.TTL_MS;
  }
}

// Singleton instance
const cosmicCache = new CosmicDataCache();

export default cosmicCache;
export { CosmicDataCache };
